import type { CommaSeparatedString } from "@@/utilities/comma-separated-string";
import { toObjectMap } from "@@/utilities/dictionary";
import { z } from "zod";

const languageCodes = ["sv", "en", "de"] as const;
type LanguageCode = (typeof languageCodes)[number];
const isLanguageCode = (value: unknown): value is LanguageCode =>
    languageCodes.includes(value as LanguageCode);
const languageCodeMap = toObjectMap<LanguageCode>([...languageCodes]);
const languageCodeZodSchema = z.enum(languageCodes);

const languageName: Record<LanguageCode, string> = {
    sv: "Svenska",
    en: "Engelska",
    de: "Tyska",
};

const languageNameNative: Record<LanguageCode, string> = {
    sv: "Svenska",
    en: "English",
    de: "Deutsch",
};

/**
 * A nested record that maps language codes to their respective translations in different languages. \
 * So to get the nam of a language in a specific language, you can use `languageNameCube[nameOf][inLanguage]`.
 *
 * @type {Record<LanguageCode, Record<LanguageCode, string>>}
 *
 * @example
 * ```typescript
 * const languageNameCube: Record<LanguageCode, Record<LanguageCode, string>> = {
 *     sv: {
 *         sv: "Svenska",
 *         en: "Swedish",
 *         de: "Schwedisch",
 *     },
 *     en: {
 *         sv: "Engelska",
 *         en: "English",
 *         de: "Englisch",
 *     },
 *     de: {
 *         sv: "Tyska",
 *         en: "German",
 *         de: "Deutsch",
 *     },
 * };
 * ```
 *
 * In this example, `languageNameCube` provides translations for the language names in Swedish (sv), English (en), and German (de).
 */
const languageNameCube: Record<LanguageCode, Record<LanguageCode, string>> = {
    sv: {
        sv: "Svenska",
        en: "Swedish",
        de: "Schwedisch",
    },
    en: {
        sv: "Engelska",
        en: "English",
        de: "Englisch",
    },
    de: {
        sv: "Tyska",
        en: "German",
        de: "Deutsch",
    },
};

/**
 * Retrieves the name of a language in a specified language.
 *
 * @param nameOf - The language code of the language whose name is to be retrieved.
 * @param inLanguage - The language code in which the name should be retrieved.
 * @returns The name of the language in the specified language.
 */
const getLanguageName = (nameOf: LanguageCode, inLanguage: LanguageCode) => {
    return languageNameCube[inLanguage][nameOf];
};

const languageCodeCsvZodSchema = z.custom<CommaSeparatedString<LanguageCode>>(
    value => {
        if (typeof value !== "string") return false;
        const languageCodes = value.split(",");
        return languageCodes.every(
            code => languageCodeZodSchema.safeParse(code).success,
        );
    },
);

export {
    getLanguageName,
    isLanguageCode,
    languageCodeCsvZodSchema,
    languageCodeMap,
    languageCodes,
    languageCodeZodSchema,
    languageName,
    languageNameCube,
    languageNameNative,
};
export type { LanguageCode };
